import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { CREATOR_STATUS_OPTIONS, LEGACY_STATUS_OPTIONS, getStatusColor } from '../constants/creatorStatus';

interface Account {
  _id: string;
  username: string;
  email?: string;
  keyword?: string;
  description?: string;
  averageViews?: number;
  medianViews?: number;
  status?: string;
  relevancy?: boolean;
  relevancyScore?: number;
  relevancyReason?: string;
  relevantKeywords?: Record<string, string[]>;
  createdAt: string;
  updatedAt: string;
  emailSentAt?: string;
  platform?: 'instagram' | 'tiktok' | 'youtube';
  // YouTube specific fields
  channelId?: string;
  channelName?: string;
  subscriberCount?: number;
  videoCount?: number;
  source?: string; // source 필드 추가
  lastPostsUpdate?: string;
  postsDateRange?: {
    from: string;
    to: string;
  };
  // Response 관련 필드
  responseStatus?: string;
  responseId?: string;
  responseTimestamp?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortField = 'username' | 'platform' | 'email' | 'keyword' | 'averageViews' | 'medianViews' | 'status' | 'responseStatus' | 'relevancy' | 'relevancyReason' | 'updatedAt' | 'emailSentAt' | 'createdAt' | 'source';
type SortOrder = 'asc' | 'desc';

// Helper function to substitute template variables
const substituteTemplateVariables = (text: string, variables: Record<string, string>): string => {
  if (!text) return '';
  
  let result = text;
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    result = result.replace(regex, value || '');
  });
  
  return result;
};

// Helper function to remove HTML tags and convert to plain text
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Replace common HTML elements with appropriate plain text formatting
  const text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Clean up extra whitespace and normalize line breaks
  return text
    .replace(/\s+/g, ' ')
    .replace(/\n\s*\n/g, '\n\n')
    .trim();
};


// Helper function to get category-specific colors
const getCategoryColor = (category: string) => {
  switch (category.toLowerCase()) {
    case 'aitools':
      return {
        bg: '#fef3c7', // yellow-100
        text: '#92400e', // yellow-800
        keywordBg: '#fffbeb', // yellow-50
        keywordText: '#92400e',
        border: '#fcd34d' // yellow-300
      };
    case 'app':
      return {
        bg: '#dbeafe', // blue-100
        text: '#1e40af', // blue-800
        keywordBg: '#eff6ff', // blue-50
        keywordText: '#1e40af',
        border: '#60a5fa' // blue-300
      };
    case 'software':
      return {
        bg: '#e5e7eb', // gray-200
        text: '#374151', // gray-700
        keywordBg: '#f9fafb', // gray-50
        keywordText: '#374151',
        border: '#9ca3af' // gray-400
      };
    case 'sidehustle':
      return {
        bg: '#dcfce7', // green-100
        text: '#166534', // green-800
        keywordBg: '#f0fdf4', // green-50
        keywordText: '#166534',
        border: '#86efac' // green-300
      };
    default:
      return {
        bg: '#f3e8ff', // purple-100
        text: '#7c3aed', // purple-600
        keywordBg: '#faf5ff', // purple-50
        keywordText: '#7c3aed',
        border: '#c4b5fd' // purple-300
      };
  }
};


interface ColumnConfig {
  key: SortField | 'select' | 'platform';
  label: string;
  width: number;
  visible: boolean;
}

interface CreatorsProps {
  isTargetMode?: boolean;
  ruleId?: string;
  initialPlatformFilter?: string;
}

const Creators: React.FC<CreatorsProps> = ({ isTargetMode = false, ruleId, initialPlatformFilter }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  // Parse initial URL parameters - comprehensive search state restoration
  const getInitialParams = () => {
    const params = new URLSearchParams(location.search);
    
    // URL 경로에서 플랫폼 추출
    let platform = params.get('platform');
    if (location.pathname === '/creators/tiktok') {
      platform = 'tiktok';
    } else if (location.pathname === '/creators/instagram') {
      platform = 'instagram';
    } else if (location.pathname === '/creators/youtube') {
      platform = 'youtube';
    }
    const keyword = params.get('keyword'); // legacy support
    const search = params.get('search'); // new unified search param
    const searchField = params.get('searchField');
    const exactMatch = params.get('exactMatch');
    const emailFilter = params.get('emailFilter');
    const emailSentFilter = params.get('emailSentFilter');
    const relevancyFilter = params.get('relevancyFilter');
    const status = params.get('status');
    const minViews = params.get('minViews');
    const maxViews = params.get('maxViews');
    const excludeSentEmails = params.get('excludeSentEmails');
    const noResponse = params.get('noResponse'); // New parameter for follow-up filtering
    
    // Use search param if available, fallback to keyword for backward compatibility
    const searchTerm = search || keyword || '';
    const searchFieldValue = searchField || (keyword ? 'keyword' : 'username');
    
    return { platform, searchTerm, searchFieldValue, exactMatch, emailFilter, emailSentFilter, relevancyFilter, status, minViews, maxViews, excludeSentEmails, noResponse };
  };
  
  const { 
    platform: initialPlatform, 
    searchTerm: initialSearchTerm,
    searchFieldValue: initialSearchFieldValue,
    exactMatch: initialExactMatch,
    emailFilter: initialEmailFilter,
    emailSentFilter: initialEmailSentFilter,
    relevancyFilter: initialRelevancyFilter,
    status: initialStatus,
    minViews: initialMinViews,
    maxViews: initialMaxViews,
    excludeSentEmails: initialExcludeSentEmails,
    noResponse: initialNoResponse
  } = getInitialParams();
  
  // Search timer ref for debouncing
  const searchTimer = useRef<NodeJS.Timeout | null>(null);
  
  const [activeTab, setActiveTab] = useState<'instagram' | 'tiktok' | 'youtube'>(() => {
    // Priority: initialPlatformFilter > URL params > default 'instagram'
    const platformToUse = initialPlatformFilter || initialPlatform;
    return (platformToUse === 'instagram' || platformToUse === 'tiktok' || platformToUse === 'youtube') ? platformToUse : 'instagram';
  });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });

  // Column configuration state - conditionally include select column
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const baseColumns: ColumnConfig[] = [
      { key: 'username', label: 'Username', width: 200, visible: true },
      { key: 'platform', label: 'Platform', width: 100, visible: true },
      { key: 'email', label: 'Email', width: 250, visible: true },
      { key: 'keyword', label: 'Keyword', width: 100, visible: true },
      { key: 'averageViews', label: 'Avg Views', width: 120, visible: false },
      { key: 'medianViews', label: 'Med Views', width: 120, visible: true },
      { key: 'relevancy', label: 'Relevancy', width: 100, visible: true },
      { key: 'relevancyReason', label: 'Relevancy Reason', width: 300, visible: false },
      { key: 'status', label: 'Status', width: 150, visible: !isTargetMode }, // Hide in target mode
      { key: 'responseStatus', label: 'Response', width: 150, visible: !isTargetMode }, // Response status
      { key: 'source', label: 'Source', width: 140, visible: true }, // source 컬럼 추가
      { key: 'emailSentAt', label: 'Email Sent', width: 140, visible: !isTargetMode }, // Hide in target mode
      { key: 'createdAt', label: 'Created', width: 140, visible: true },
      { key: 'updatedAt', label: 'Updated', width: 140, visible: false },
    ];
    
    // Always add select column, even in target mode
    baseColumns.unshift({ key: 'select', label: 'Select', width: 60, visible: true } as any);
    
    return baseColumns;
  });

  // Column customization state
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);

  // CSV Export state
  const [showCsvExportModal, setShowCsvExportModal] = useState(false);
  const [csvExportColumns, setCsvExportColumns] = useState<string[]>([]);

  // Extract Emails state (YouTube only)
  const [showExtractEmailsModal, setShowExtractEmailsModal] = useState(false);
  const [extractEmailsMinViews, setExtractEmailsMinViews] = useState(5000);
  const [extractEmailsCountry, setExtractEmailsCountry] = useState<string>('all');
  const [extractEmailsProgress, setExtractEmailsProgress] = useState<{
    status: 'idle' | 'processing' | 'complete';
    progress: number;
    total: number;
    current: number;
    success: number;
    failed: number;
  } | null>(null);
  const [extractEmailsPreviewCount, setExtractEmailsPreviewCount] = useState<number>(0);
  const [extractEmailsLoading, setExtractEmailsLoading] = useState(false);

  // Extract Emails state (TikTok)
  const [showTikTokExtractEmailsModal, setShowTikTokExtractEmailsModal] = useState(false);
  const [tiktokExtractEmailsMinViews, setTiktokExtractEmailsMinViews] = useState(5000);
  const [tiktokExtractEmailsProgress, setTiktokExtractEmailsProgress] = useState<{
    status: 'idle' | 'processing' | 'complete';
    progress: number;
    total: number;
    current: number;
    success: number;
    failed: number;
  } | null>(null);
  const [tiktokExtractEmailsPreviewCount, setTiktokExtractEmailsPreviewCount] = useState<number>(0);
  const [tiktokExtractEmailsLoading, setTiktokExtractEmailsLoading] = useState(false);

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  
  // Sort state - Load from localStorage or use defaults
  const [sortField, setSortField] = useState<SortField>(() => {
    const saved = localStorage.getItem('creators_sortField');
    return (saved as SortField) || 'updatedAt';
  });
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('creators_sortOrder');
    return (saved as SortOrder) || 'desc';
  });
  
  // Search state - initialize with URL parameters (both legacy keyword and new search)
  const [searchTerm, setSearchTerm] = useState(() => {
    const value = initialSearchTerm || '';
    return value;
  });
  const [searchInput, setSearchInput] = useState(() => {
    const value = initialSearchTerm || '';
    return value;
  });
  const [searchField, setSearchField] = useState<'all' | 'username' | 'email' | 'keyword'>(() => {
    const value = initialSearchFieldValue as 'all' | 'username' | 'email' | 'keyword' || 'username';
    return value;
  });
  const [exactMatch, setExactMatch] = useState(() => {
    const value = initialExactMatch === 'true';
    return value;
  });

  // Handle navigation from AutomationLogs with specific creator
  useEffect(() => {
    if (location.state) {
      const { searchUsername, platform } = location.state as { searchUsername?: string; platform?: 'tiktok' | 'instagram' };
      
      if (searchUsername) {
        // Set the platform tab if provided
        if (platform && (platform === 'tiktok' || platform === 'instagram')) {
          setActiveTab(platform);
        }
        
        // Set the search parameters
        setSearchField('username');
        setSearchTerm(searchUsername);
        setSearchInput(searchUsername);
        setExactMatch(true);
        
        // Reset pagination to first page
        setPagination(prev => ({ ...prev, page: 1 }));
        
        // Clear the location state to prevent re-triggering
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.state, navigate, location.pathname]);
  
  // Views filter state
  const [minViews, setMinViews] = useState<number>(initialMinViews ? parseInt(initialMinViews) : 0);
  const [maxViews, setMaxViews] = useState<number | null>(initialMaxViews ? parseInt(initialMaxViews) : null);
  const [minViewsInput, setMinViewsInput] = useState(initialMinViews || '');
  const [maxViewsInput, setMaxViewsInput] = useState(initialMaxViews || '');
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus || 'all');
  
  // Email sending state
  const [sendingEmail, setSendingEmail] = useState<string | null>(null); // Store the account ID that's currently sending
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<Account | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Test email state
  const [showTestEmailInput, setShowTestEmailInput] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Relevancy filter state
  const [relevancyFilter, setRelevancyFilter] = useState<string>(initialRelevancyFilter || 'all');
  
  // Email filter state
  const [emailFilter, setEmailFilter] = useState<string>(initialEmailFilter || 'all');
  const [emailSentFilter, setEmailSentFilter] = useState<string>(initialEmailSentFilter || 'all');
  const [excludeSentEmails, setExcludeSentEmails] = useState<boolean>(initialExcludeSentEmails === 'true');
  
  // Page input state
  const [pageInput, setPageInput] = useState('');
  
  // Multi-select state
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Column filters state
  const [columnFilters, setColumnFilters] = useState<Record<string, Set<string>>>({});
  const [showFilter, setShowFilter] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({});

  // Lazy loading for recent posts

  // CRUD states for creators
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [editingCreator, setEditingCreator] = useState<Account | null>(null);
  const [creatorForm, setCreatorForm] = useState({
    platform: 'tiktok' as 'instagram' | 'tiktok' | 'youtube',
    username: '',
    email: '',
    keyword: '',
    description: '',
    averageViews: '',
    status: '',
    relevancy: false
  });

  // Import from others modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importKeywords, setImportKeywords] = useState<string[]>([]);
  const [availableKeywords, setAvailableKeywords] = useState<{ keyword: string; count: number; usernames: string[] }[]>([]);
  const [minMedianViews, setMinMedianViews] = useState<number>(10000);
  const [importOnlyWithEmail, setImportOnlyWithEmail] = useState(true);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null);




  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilter && !(event.target as Element).closest('.filter-dropdown')) {
        setShowFilter(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilter]);

  // Generate filter options from current data
  useEffect(() => {
    if (accounts.length > 0) {
      const options: Record<string, string[]> = {};
      
      // Get unique values for each filterable column
      const filterableColumns = ['email', 'keyword', 'status'];
      
      filterableColumns.forEach(column => {
        const values = accounts.map(account => {
          const value = (account as any)[column];
          return value || 'Empty';
        }).filter(Boolean);
        
        const uniqueValues = Array.from(new Set(values)).sort();
        options[column] = uniqueValues;
      });
      
      setFilterOptions(options);
    }
  }, [accounts]);

  // Fetch accounts with proper pagination
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      page: pagination.page.toString(),
      limit: '100',
      sortBy: sortField,
      sortOrder: sortOrder
    });
    
    // 검색 파라미터 추가
    if (searchTerm) {
      params.append('search', searchTerm);
      params.append('searchField', searchField);
      params.append('exactMatch', exactMatch.toString());
    }

    // Email filter
    if (emailFilter && emailFilter !== 'all') {
      params.append('emailFilter', emailFilter);
    }
    
    // Target mode일 때는 email-rules API 사용
    let url: string;
    if (isTargetMode && ruleId) {
      const targetParams = new URLSearchParams({
        page: pagination.page.toString(),
        limit: '100',
        search: searchTerm,
        platform: activeTab,
        sortBy: sortField === 'username' ? 'createdAt' : sortField,
        sortOrder: sortOrder
      });
      url = `${API_URL}/api/email-rules/${ruleId}/targets?${targetParams}`;
    } else {
      // 플랫폼별 라우트 사용
      if (activeTab === 'tiktok') {
        url = `${API_URL}/api/creators/tiktok?${params}`;
      } else if (activeTab === 'instagram') {
        url = `${API_URL}/api/creators/instagram?${params}`;
      } else if (activeTab === 'youtube') {
        url = `${API_URL}/api/youtube/accounts?${params}`;
      } else {
        // 기본값은 기존 통합 라우트 사용 (모든 플랫폼)
        params.append('platform', activeTab);
        url = `${API_URL}/api/creators?${params}`;
      }
    }
    try {
      const response = await fetch(url, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle both regular API and email-rules API responses
      if (data.success) {
        
        setAccounts(data.data);
        
        // Handle different pagination structures
        if (data.pagination) {
          setPagination(data.pagination);
        } else {
          // For email-rules API response
          setPagination({
            page: data.page || 1,
            limit: data.limit || 100,
            total: data.total || 0,
            totalPages: Math.ceil((data.total || 0) / (data.limit || 100))
          });
        }
        
        // Clear selections when data changes
        setSelectedAccounts(new Set());
        setSelectAll(false);
      } else {
        setError(data.error || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [activeTab, sortField, sortOrder, searchTerm, searchField, exactMatch, minViews, maxViews, statusFilter, relevancyFilter, emailFilter, emailSentFilter, excludeSentEmails, pagination.page, isTargetMode, ruleId, token]);

  // Fetch distinct keywords for import modal (with minMedianViews and email filter)
  const fetchDistinctKeywords = async (minViews?: number, onlyWithEmail?: boolean) => {
    setIsLoadingKeywords(true);
    try {
      const viewsParam = minViews !== undefined ? minViews : minMedianViews;
      const emailParam = onlyWithEmail !== undefined ? onlyWithEmail : importOnlyWithEmail;
      const response = await fetch(`${API_URL}/api/creators/distinct-keywords?platform=${activeTab}&minMedianViews=${viewsParam}&onlyWithEmail=${emailParam}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setAvailableKeywords(data.data);
        // Clear selected keywords that no longer have importable creators
        const availableKeywordSet = new Set(data.data.map((k: { keyword: string }) => k.keyword));
        setImportKeywords(prev => prev.filter(k => availableKeywordSet.has(k)));
      }
    } catch (error) {
      console.error('Error fetching distinct keywords:', error);
      toast.error('Failed to load keywords');
    } finally {
      setIsLoadingKeywords(false);
    }
  };

  // Execute import from other accounts
  const executeImport = async () => {
    if (importKeywords.length === 0) {
      toast.error('Please select at least one keyword');
      return;
    }

    setIsImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/creators/import-from-others`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          platform: activeTab,
          keywords: importKeywords,
          minMedianViews: minMedianViews,
          onlyWithEmail: importOnlyWithEmail,
          limit: 500
        })
      });

      const data = await response.json();
      if (data.success) {
        const { imported, skipped, total } = data.data;
        if (imported > 0) {
          toast.success(`Imported ${imported} creators${skipped > 0 ? ` (${skipped} duplicates skipped)` : ''}`);
          fetchAccounts(); // Refresh the list
        } else if (total === 0) {
          toast.info('No creators found matching the criteria');
        } else {
          toast.info(`All ${skipped} matching creators already exist in your account`);
        }
        setShowImportModal(false);
        setImportKeywords([]);
      } else {
        toast.error(data.error || 'Failed to import creators');
      }
    } catch (error) {
      console.error('Error importing creators:', error);
      toast.error('Failed to import creators');
    } finally {
      setIsImporting(false);
    }
  };

  // CRUD functions for creators
  const createCreator = async () => {
    // Optimistic update: immediately add to UI
    const tempId = `temp_${Date.now()}`;
    const newCreator: Account = {
      _id: tempId,
      username: creatorForm.username,
      email: creatorForm.email || undefined,
      keyword: creatorForm.keyword || undefined,
      description: creatorForm.description || undefined,
      averageViews: creatorForm.averageViews ? parseInt(creatorForm.averageViews) : undefined,
      status: creatorForm.status || undefined,
      relevancy: creatorForm.relevancy || false,
      platform: creatorForm.platform,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Add to UI immediately
    setAccounts(prev => [newCreator, ...prev]);
    setPagination(prev => ({ ...prev, total: prev.total + 1 }));
    setShowCreatorModal(false);
    resetCreatorForm();
    
    // Then send to backend
    try {
      const response = await fetch(`${API_URL}/api/creators`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          platform: creatorForm.platform,
          username: creatorForm.username,
          email: creatorForm.email || undefined,
          keyword: creatorForm.keyword || undefined,
          description: creatorForm.description || undefined,
          averageViews: creatorForm.averageViews ? parseInt(creatorForm.averageViews) : undefined,
          status: creatorForm.status || undefined,
          relevancy: creatorForm.relevancy
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Replace temp creator with real one from backend
        setAccounts(prev => prev.map(acc => acc._id === tempId ? data.data : acc));
      } else {
        // Rollback on error
        setAccounts(prev => prev.filter(acc => acc._id !== tempId));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
        const data = await response.json();
        setError(data.error || 'Failed to create creator');
      }
    } catch (error) {
      // Rollback on error
      setAccounts(prev => prev.filter(acc => acc._id !== tempId));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      setError('Failed to create creator');
      console.error('Error creating creator:', error);
    }
  };

  const updateCreator = async () => {
    if (!editingCreator) return;
    
    try {
      const response = await fetch(`${API_URL}/api/creators/${editingCreator.platform}/${editingCreator._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          username: creatorForm.username,
          email: creatorForm.email || undefined,
          keyword: creatorForm.keyword || undefined,
          description: creatorForm.description || undefined,
          averageViews: creatorForm.averageViews ? parseInt(creatorForm.averageViews) : undefined,
          status: creatorForm.status || undefined,
          relevancy: creatorForm.relevancy
        }),
      });

      if (response.ok) {
        setShowCreatorModal(false);
        setEditingCreator(null);
        resetCreatorForm();
        fetchAccounts();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update creator');
      }
    } catch (error) {
      setError('Failed to update creator');
      console.error('Error updating creator:', error);
    }
  };

  const deleteCreator = async (creator: Account) => {
    if (!window.confirm('Are you sure you want to delete this creator?')) return;
    
    // Optimistic update: immediately remove from UI
    const originalAccounts = accounts;
    const originalPagination = pagination;
    
    setAccounts(prev => prev.filter(acc => acc._id !== creator._id));
    setPagination(prev => ({ ...prev, total: prev.total - 1 }));
    setSelectedAccounts(prev => {
      const newSet = new Set(prev);
      newSet.delete(creator._id);
      return newSet;
    });
    
    // Then send to backend
    try {
      const platform = creator.platform || activeTab;
      const response = await fetch(`${API_URL}/api/creators/${platform}/${creator._id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        // Refresh the data after successful deletion
        fetchAccounts();
      } else {
        // Rollback on error
        setAccounts(originalAccounts);
        setPagination(originalPagination);
        const data = await response.json();
        setError(data.error || 'Failed to delete creator');
      }
    } catch (error) {
      // Rollback on error
      setAccounts(originalAccounts);
      setPagination(originalPagination);
      setError('Failed to delete creator');
      console.error('Error deleting creator:', error);
    }
  };

  // Multi-select functions
  const toggleSelectAccount = (accountId: string, index: number, event?: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
    // Type guard to check if it's a MouseEvent
    const isMouseEvent = event && 'shiftKey' in event;
    const shiftKey = isMouseEvent ? event.shiftKey : false;
    const cmdKey = isMouseEvent ? (event.metaKey || event.ctrlKey) : false;
    
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift + Click: Select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      setSelectedAccounts(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          if (filteredAccounts[i]) {
            newSet.add(filteredAccounts[i]._id);
          }
        }
        return newSet;
      });
    } else if (cmdKey) {
      // Cmd/Ctrl + Click: Toggle individual selection
      setSelectedAccounts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(accountId)) {
          newSet.delete(accountId);
        } else {
          newSet.add(accountId);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    } else {
      // Normal click: Toggle selection (add to existing selection)
      setSelectedAccounts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(accountId)) {
          newSet.delete(accountId);
        } else {
          newSet.add(accountId);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedAccounts(new Set());
      setSelectAll(false);
      setLastSelectedIndex(null);
    } else {
      setSelectedAccounts(new Set(filteredAccounts.map(account => account._id)));
      setSelectAll(true);
    }
  };

  const deleteSelectedCreators = async () => {
    if (selectedAccounts.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedAccounts.size} selected creators?`)) return;
    
    // Optimistic update: immediately remove from UI
    const originalAccounts = accounts;
    const originalPagination = pagination;
    const deletedIds = Array.from(selectedAccounts);
    
    setAccounts(prev => prev.filter(acc => !selectedAccounts.has(acc._id)));
    setPagination(prev => ({ ...prev, total: prev.total - selectedAccounts.size }));
    setSelectedAccounts(new Set());
    setSelectAll(false);
    setLastSelectedIndex(null);
    
    // Then send to backend
    try {
      const deletePromises = deletedIds.map(accountId => {
        const account = originalAccounts.find(a => a._id === accountId);
        if (!account) return Promise.resolve({ ok: true });

        const platform = account.platform || activeTab;
        // For YouTube, use channelId instead of _id
        const identifier = platform === 'youtube' ? account.channelId : accountId;
        const endpoint = platform === 'youtube'
          ? `${API_URL}/api/youtube/accounts/${identifier}`
          : `${API_URL}/api/creators/${platform}/${identifier}`;

        return fetch(endpoint, {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
      });

      const results = await Promise.all(deletePromises);
      const failedDeletes = results.filter(result => result && !result.ok);
      
      if (failedDeletes.length > 0) {
        // Partial rollback: restore failed deletes
        const failedIds = new Set<string>();
        results.forEach((result, index) => {
          if (result && !result.ok) {
            failedIds.add(deletedIds[index]);
          }
        });
        
        const restoredAccounts = originalAccounts.filter(acc => failedIds.has(acc._id));
        setAccounts(prev => [...prev, ...restoredAccounts]);
        setPagination(prev => ({ ...prev, total: prev.total + failedIds.size }));
        setError(`Failed to delete ${failedDeletes.length} out of ${selectedAccounts.size} creators`);
      } else {
        // All deletions successful, refresh the data
        fetchAccounts();
      }
    } catch (error) {
      // Full rollback on error
      setAccounts(originalAccounts);
      setPagination(originalPagination);
      setError('Failed to delete selected creators');
      console.error('Error deleting creators:', error);
    }
  };

  const updateSelectedCreatorsRelevancy = async (relevancy: boolean) => {
    if (selectedAccounts.size === 0) return;
    
    const relevancyText = relevancy ? 'relevant' : 'not relevant';
    if (!window.confirm(`Are you sure you want to mark ${selectedAccounts.size} selected creators as ${relevancyText}?`)) return;
    
    // Optimistic update: immediately update in UI
    const originalAccounts = accounts;
    const updatedIds = Array.from(selectedAccounts);
    
    setAccounts(prev => prev.map(acc => 
      selectedAccounts.has(acc._id) 
        ? { ...acc, relevancy }
        : acc
    ));
    setSelectedAccounts(new Set());
    setSelectAll(false);
    setLastSelectedIndex(null);
    
    // Then send to backend
    try {
      const updatePromises = updatedIds.map(accountId => {
        const account = originalAccounts.find(a => a._id === accountId);
        if (!account) return Promise.resolve({ ok: true });
        
        const platform = account.platform || activeTab;
        return fetch(`${API_URL}/api/creators/${platform}/${accountId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ relevancy }),
        });
      });

      const results = await Promise.all(updatePromises);
      const failedUpdates = results.filter(result => result && !result.ok);
      
      if (failedUpdates.length > 0) {
        // Partial rollback: restore failed updates
        const failedIds = new Set<string>();
        results.forEach((result, index) => {
          if (result && !result.ok) {
            failedIds.add(updatedIds[index]);
          }
        });
        
        setAccounts(prev => prev.map(acc => {
          if (failedIds.has(acc._id)) {
            const originalAccount = originalAccounts.find(orig => orig._id === acc._id);
            return originalAccount ? { ...acc, relevancy: originalAccount.relevancy } : acc;
          }
          return acc;
        }));
        setError(`Failed to update ${failedUpdates.length} out of ${updatedIds.length} creators`);
      } else {
        // All updates successful, refresh the data
        fetchAccounts();
      }
    } catch (error) {
      // Full rollback on error
      setAccounts(originalAccounts);
      setError('Failed to update selected creators');
      console.error('Error updating creators relevancy:', error);
    }
  };

  // Reanalyze selected creators
  const reanalyzeSelectedCreators = async () => {
    if (selectedAccounts.size === 0) return;

    const platform = activeTab;
    const platformName = platform === 'tiktok' ? 'TikTok' : platform === 'instagram' ? 'Instagram' : 'YouTube';

    if (!window.confirm(`Reanalyze ${selectedAccounts.size} selected ${platformName} creator${selectedAccounts.size > 1 ? 's' : ''}?\n\nThis will update median views and relevancy data.`)) return;

    setLoading(true);
    setError(null);

    try {
      const creatorIds = Array.from(selectedAccounts);

      const response = await fetch(`${API_URL}/api/creators/reanalyze-selected`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ platform, creatorIds }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Started reanalysis for ${result.total} ${platformName} creator${result.total > 1 ? 's' : ''}. Refresh in a few moments.`, {
          autoClose: 5000
        });

        // Refresh after a delay
        setTimeout(() => {
          fetchAccounts();
          setSelectedAccounts(new Set());
          setSelectAll(false);
        }, 5000);
      } else {
        setError(result.error || 'Failed to start reanalysis');
      }
    } catch (error) {
      console.error('Error reanalyzing selected creators:', error);
      setError('Failed to reanalyze selected creators');
    } finally {
      setLoading(false);
    }
  };

  // Column filter functions
  const toggleColumnFilter = (column: string, value: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[column]) {
        newFilters[column] = new Set();
      }
      
      if (newFilters[column].has(value)) {
        newFilters[column].delete(value);
        if (newFilters[column].size === 0) {
          delete newFilters[column];
        }
      } else {
        newFilters[column].add(value);
      }
      
      return newFilters;
    });
  };

  const clearColumnFilter = (column: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    // Clear any pending search timer
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    
    setColumnFilters({});
    setSearchInput('');
    setSearchTerm('');
    setMinViews(0);
    setMaxViews(null);
    setMinViewsInput('');
    setMaxViewsInput('');
    setStatusFilter('all');
    setEmailFilter('all');
    setEmailSentFilter('all');
    setRelevancyFilter('all');
    setExcludeSentEmails(false);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Clear URL parameters
    const searchParams = new URLSearchParams();
    // 새로운 경로 구조에서는 platform 파라미터 불필요
    const newPath = `/creators/${activeTab}`;
    navigate(`${newPath}?${searchParams.toString()}`, { replace: true });
  };

  // Bulk analyze creators
  const bulkAnalyzeCreators = async () => {
    const platform = activeTab;
    const platformName = platform === 'tiktok' ? 'TikTok' : platform === 'instagram' ? 'Instagram' : 'YouTube';

    setLoading(true);
    setError(null);

    try {

      // First, call the API to get the count
      const response = await fetch(`${API_URL}/api/creators/bulk-reanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ platform }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.total === 0) {
          toast.info(`No ${platformName} creators need analysis. All creators are already analyzed.`);
          setLoading(false);
          return;
        }

        // Show confirmation with count
        const confirmMessage = `Found ${result.total} unanalyzed ${platformName} creator${result.total > 1 ? 's' : ''}.\n\nStart bulk analysis in background?`;

        if (!window.confirm(confirmMessage)) {
          setLoading(false);
          return;
        }

        console.log('✅ Bulk analysis started:', result);

        // Show progress toast
        const toastId = toast.info(`Analyzing ${platformName} creators: 0/${result.total} (0%)`, {
          autoClose: false,
          closeButton: false
        });

        // Poll progress
        const pollInterval = setInterval(async () => {
          try {
            const progressRes = await fetch(`${API_URL}/api/creators/bulk-reanalyze-progress/${platform}`, {
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
              }
            });
            const progressData = await progressRes.json();

            if (progressData.success && progressData.progress) {
              const { progress, current, total, status, success, failed } = progressData.progress;

              if (status === 'complete') {
                clearInterval(pollInterval);
                toast.dismiss(toastId);
                toast.success(`✅ Analysis complete! ${success} succeeded, ${failed} failed.`, {
                  autoClose: 5000
                });
                fetchAccounts();
              } else {
                toast.update(toastId, {
                  render: `Analyzing ${platformName} creators: ${current}/${total} (${progress}%)`,
                });
              }
            }
          } catch (pollError) {
            console.error('Error polling progress:', pollError);
          }
        }, 2000);

        // Auto-stop polling after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          toast.dismiss(toastId);
        }, 600000);

      } else {
        console.error('Bulk analysis failed:', result);
        setError(result.error || 'Failed to start bulk analysis');
      }
    } catch (error) {
      console.error('Error starting bulk analysis:', error);
      setError('Failed to start bulk analysis');
    } finally {
      setLoading(false);
    }
  };

  // Clear low-quality creators (all platforms)
  const clearLowQualityCreators = async () => {
    const platform = activeTab;
    const platformName = platform === 'tiktok' ? 'TikTok' : platform === 'instagram' ? 'Instagram' : 'YouTube';

    const confirmMessage = `이 작업은 다음 조건의 ${platformName} 크리에이터들을 삭제합니다:
• relevancy = false 인 크리에이터
• relevancyScore < 0.3 인 크리에이터
• 분석 실패한 크리에이터
• 포스트가 없는 크리에이터

정말로 진행하시겠습니까?`;

    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`🗑️ Clearing low-quality ${platform} creators...`);

      const response = await fetch(`${API_URL}/api/creators/clear-low-quality`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ platform }),
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Clear operation completed:', result);
        alert(`성공적으로 ${result.deletedCount}명의 저품질 크리에이터를 삭제했습니다.`);

        // Refresh the data
        fetchAccounts();
      } else {
        console.error('Clear operation failed:', result);
        setError(result.error || 'Failed to clear low-quality creators');
      }
    } catch (error) {
      console.error('Error clearing low-quality creators:', error);
      setError('Network error occurred while clearing creators');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  // Lazy load recent posts for a creator
  const resetCreatorForm = () => {
    setCreatorForm({
      platform: activeTab,
      username: '',
      email: '',
      keyword: '',
      description: '',
      averageViews: '',
      status: '',
      relevancy: false
    });
  };


  const openCreateModal = () => {
    // Only allow creating for Instagram and TikTok platforms
    if (activeTab !== 'instagram' && activeTab !== 'tiktok') {
      alert('Manual creator addition is only available for Instagram and TikTok platforms.');
      return;
    }
    setEditingCreator(null);
    resetCreatorForm();
    setShowCreatorModal(true);
  };

  const openEditModal = (creator: Account) => {
    setEditingCreator(creator);
    setCreatorForm({
      platform: creator.platform || activeTab,
      username: creator.username,
      email: creator.email || '',
      keyword: creator.keyword || '',
      description: creator.description || '',
      averageViews: creator.averageViews ? creator.averageViews.toString() : '',
      status: creator.status || '',
      relevancy: Boolean(creator.relevancy)
    });
    setShowCreatorModal(true);
  };

  // Fetch templates for email sending
  const fetchTemplates = async () => {
    if (templates.length > 0) return; // Already loaded
    
    setLoadingTemplates(true);
    try {
      const response = await fetch(`${API_URL}/api/templates`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter for email templates only
          const emailTemplates = data.data.filter((template: any) => template.type === 'email');
          setTemplates(emailTemplates);
          
          // Auto-select first email template if available
          if (emailTemplates.length > 0) {
            setSelectedTemplate(emailTemplates[0]._id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Open email modal for a creator
  const openEmailModal = async (account: Account) => {
    if (!account.email) {
      alert('This creator does not have an email address.');
      return;
    }

    setSelectedCreator(account);
    setEmailAddress(account.email);
    setShowEmailModal(true);
    
    // Fetch templates if not already loaded
    await fetchTemplates();
  };

  // Handle sending email to a creator (updated to use template system)
  const handleSendEmail = async () => {
    if (!selectedCreator || !emailAddress || !selectedTemplate) {
      alert('Please select a template and verify the email address.');
      return;
    }

    if (sendingEmail === selectedCreator._id) {
      return; // Prevent double-clicking
    }

    setSendingEmail(selectedCreator._id);

    try {
      // Get the selected template
      const template = templates.find(t => t._id === selectedTemplate);
      if (!template) {
        alert('Selected template not found.');
        return;
      }

      console.log(`Sending email to ${emailAddress} using template ${template.name}`);
      
      // Prepare variables for template generation (similar to outreach)
      const templateVariables: Record<string, string> = {
        name: selectedCreator.username,
        handle: `@${selectedCreator.username}`,
        email: emailAddress,
        platform: selectedCreator.platform || activeTab,
        username: selectedCreator.username,
        averageViews: selectedCreator.averageViews?.toString() || '0',
        followerCount: (selectedCreator as any).followerCount?.toString() || 'N/A'
      };

      // Generate email content using template API
      const generateResponse = await fetch(`${API_URL}/api/templates/${template._id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ variables: templateVariables })
      });

      if (!generateResponse.ok) {
        throw new Error('Failed to generate email content from template');
      }

      const generatedContent = await generateResponse.json();
      console.log('Generated content:', generatedContent);

      // Prepare email payload
      const emailPayload = {
        to: emailAddress,
        subject: generatedContent.data?.subject || template.subject || `Collaboration Opportunity - ${selectedCreator.username}`,
        content: generatedContent.data?.content || template.content,
        templateId: template._id,
        templateType: template.type,
        templateCategory: template.category,
        metadata: {
          platform: selectedCreator.platform || activeTab,
          username: selectedCreator.username,
          recipientName: selectedCreator.username,
          isTest: false,
          ruleId: 'manual-outreach-creators',
          ruleName: 'Manual Outreach from Creators Page',
          source: 'creators_page'
        }
      };

      console.log('Email payload:', emailPayload);

      // Send the email
      const response = await fetch(`${API_URL}/api/sent-emails/send-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(emailPayload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Email sent successfully:', result);
        
        // Update the account's emailSentAt field locally
        setAccounts(prev => prev.map(acc => 
          acc._id === selectedCreator._id 
            ? { ...acc, emailSentAt: new Date().toISOString() }
            : acc
        ));

        // Update the creator's emailSentAt in the backend
        try {
          const platform = selectedCreator.platform || activeTab;
          await fetch(`${API_URL}/api/creators/${platform}/${selectedCreator._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify({
              emailSentAt: new Date().toISOString()
            })
          });
        } catch (updateError) {
          console.error('Error updating emailSentAt:', updateError);
          // Don't show error to user as email was sent successfully
        }

        alert(`✅ Email sent successfully to ${emailAddress} using template "${template.name}"!`);
        
        // Close modal and reset
        setShowEmailModal(false);
        setSelectedCreator(null);
        setEmailAddress('');
      } else {
        const errorData = await response.json();
        console.error('Failed to send email:', errorData);
        alert(`❌ Failed to send email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`❌ Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingEmail(null);
    }
  };

  // Handle sending test email
  const handleSendTestEmail = async () => {
    if (!selectedCreator || !testEmailAddress || !selectedTemplate) {
      alert('Please enter a test email address and select a template.');
      return;
    }

    if (!testEmailAddress.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    if (sendingTestEmail) {
      return; // Prevent double-clicking
    }

    setSendingTestEmail(true);

    try {
      // Get the selected template
      const template = templates.find(t => t._id === selectedTemplate);
      if (!template) {
        alert('Selected template not found.');
        return;
      }

      console.log(`Sending test email to ${testEmailAddress} using template ${template.name}`);
      
      // Prepare variables for template generation (similar to regular email)
      const templateVariables: Record<string, string> = {
        name: selectedCreator.username,
        handle: `@${selectedCreator.username}`,
        email: emailAddress, // Use original creator email in template variables
        platform: selectedCreator.platform || activeTab,
        username: selectedCreator.username,
        averageViews: selectedCreator.averageViews?.toString() || '0',
        followerCount: (selectedCreator as any).followerCount?.toString() || 'N/A'
      };

      // Generate email content using template API
      const generateResponse = await fetch(`${API_URL}/api/templates/${template._id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ variables: templateVariables })
      });

      if (!generateResponse.ok) {
        throw new Error('Failed to generate email content from template');
      }

      const generatedContent = await generateResponse.json();
      console.log('Generated test content:', generatedContent);

      // Prepare email payload for test email
      const emailPayload = {
        to: testEmailAddress, // Send to test email address
        subject: `[TEST] ${generatedContent.data?.subject || template.subject || `Test Email - ${selectedCreator.username}`}`,
        content: generatedContent.data?.content || template.content,
        templateId: template._id,
        templateType: template.type,
        templateCategory: template.category,
        metadata: {
          platform: selectedCreator.platform || activeTab,
          username: selectedCreator.username,
          recipientName: 'Test User',
          isTest: true, // Mark as test email
          ruleId: 'manual-test-creators',
          ruleName: 'Test Email from Creators Page',
          source: 'creators_page_test',
          originalRecipient: emailAddress // Store original recipient for reference
        }
      };

      console.log('Test email payload:', emailPayload);

      // Send the test email
      const response = await fetch(`${API_URL}/api/sent-emails/send-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(emailPayload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Test email sent successfully:', result);
        
        alert(`✅ Test email sent successfully to ${testEmailAddress} using template "${template.name}"!\n\nThis was a test email with ${selectedCreator.username}'s information.`);
        
        // Reset test email input
        setTestEmailAddress('');
        setShowTestEmailInput(false);
      } else {
        const errorData = await response.json();
        console.error('Failed to send test email:', errorData);
        alert(`❌ Failed to send test email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert(`❌ Failed to send test email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Handle URL query parameters when they change after initial load
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    // Skip the initial mount since we handle that in useState initialization
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const params = new URLSearchParams(location.search);
    const platformParam = params.get('platform');
    const searchParam = params.get('search');
    const keywordParam = params.get('keyword'); // legacy support
    const searchFieldParam = params.get('searchField');
    const exactMatchParam = params.get('exactMatch');
    
    // Update platform if it changed
    if (platformParam === 'instagram' || platformParam === 'tiktok') {
      if (activeTab !== platformParam) {
        setActiveTab(platformParam);
      }
    }
    
    // Handle search parameters - use new search param or legacy keyword
    const urlSearchTerm = searchParam || keywordParam || '';
    const urlSearchField = searchFieldParam || (keywordParam ? 'keyword' : 'username');
    const urlExactMatch = exactMatchParam === 'true';
    
    // Only update if URL values differ from current state
    if (urlSearchTerm !== searchInput || urlSearchField !== searchField || urlExactMatch !== exactMatch) {
      console.log('Syncing search state from URL:', { urlSearchTerm, urlSearchField, urlExactMatch });
      setSearchInput(urlSearchTerm);
      setSearchTerm(urlSearchTerm);
      setSearchField(urlSearchField as 'all' | 'username' | 'email' | 'keyword');
      setExactMatch(urlExactMatch);
    }
  }, [location.search]);

  // Fetch accounts when dependencies change
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Reset page to 1 when sort or filters change (excluding search - handled in handleSearch)
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, minViews, maxViews, statusFilter, relevancyFilter, emailFilter, emailSentFilter]);

  // CSV Export functions
  const openCsvExportModal = () => {
    // Initialize with currently visible columns (excluding 'select')
    const visibleColumns = columns
      .filter(col => col.visible && col.key !== 'select')
      .map(col => col.key);
    setCsvExportColumns(visibleColumns);
    setShowCsvExportModal(true);
  };

  const toggleCsvColumn = (columnKey: string) => {
    setCsvExportColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const selectAllCsvColumns = () => {
    const allColumns = columns
      .filter(col => col.key !== 'select')
      .map(col => col.key);
    setCsvExportColumns(allColumns);
  };

  const deselectAllCsvColumns = () => {
    setCsvExportColumns([]);
  };

  const exportToCsv = () => {
    if (csvExportColumns.length === 0) {
      alert('Please select at least one column to export');
      return;
    }

    // Get column labels for headers
    const headers = csvExportColumns.map(key => {
      const col = columns.find(c => c.key === key);
      return col?.label || key;
    });

    // Build CSV rows
    const rows = filteredAccounts.map((account: Account) => {
      return csvExportColumns.map(key => {
        let value = (account as any)[key];

        // Format specific fields
        if (key === 'createdAt' || key === 'updatedAt' || key === 'emailSentAt') {
          value = value ? new Date(value).toLocaleString() : '';
        } else if (key === 'relevancy') {
          value = value === true ? 'Relevant' : value === false ? 'Not Relevant' : 'Unknown';
        } else if (key === 'averageViews' || key === 'medianViews') {
          value = value ? Number(value).toLocaleString() : '';
        } else if (value === null || value === undefined) {
          value = '';
        }

        // Escape CSV special characters
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    });

    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows].join('\n');

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}_creators_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setShowCsvExportModal(false);
  };

  // Extract Emails functions (YouTube only)
  const fetchExtractEmailsPreview = async () => {
    setExtractEmailsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/youtube/accounts/count-for-email-extraction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          minMedianViews: extractEmailsMinViews,
          country: extractEmailsCountry === 'all' ? null : extractEmailsCountry
        })
      });
      const result = await response.json();
      if (result.success) {
        setExtractEmailsPreviewCount(result.count);
      }
    } catch (error) {
      console.error('Error fetching preview count:', error);
    } finally {
      setExtractEmailsLoading(false);
    }
  };

  const startEmailExtraction = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/youtube/accounts/bulk-extract-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          minMedianViews: extractEmailsMinViews,
          country: extractEmailsCountry === 'all' ? null : extractEmailsCountry
        })
      });
      const result = await response.json();
      if (result.success) {
        // Start polling progress
        pollExtractionProgress();
      } else {
        alert(result.error || 'Failed to start extraction');
      }
    } catch (error) {
      console.error('Error starting extraction:', error);
      alert('Failed to start extraction');
    }
  };

  const pollExtractionProgress = () => {
    const token = localStorage.getItem('authToken');
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/youtube/accounts/bulk-extract-emails-progress`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const result = await response.json();
        if (result.success) {
          setExtractEmailsProgress(result.progress);
          if (result.progress.status === 'complete') {
            clearInterval(interval);
            // Refresh accounts list
            fetchAccounts();
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 2000);
  };

  // Fetch preview count when modal opens or filters change
  useEffect(() => {
    if (showExtractEmailsModal) {
      fetchExtractEmailsPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExtractEmailsModal, extractEmailsMinViews, extractEmailsCountry]);

  // TikTok Extract Emails functions
  const fetchTikTokExtractEmailsPreview = async () => {
    setTiktokExtractEmailsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/tiktok/accounts/count-for-email-extraction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          minMedianViews: tiktokExtractEmailsMinViews
        })
      });
      const result = await response.json();
      if (result.success) {
        setTiktokExtractEmailsPreviewCount(result.count);
      }
    } catch (error) {
      console.error('Error fetching TikTok email extraction preview:', error);
    } finally {
      setTiktokExtractEmailsLoading(false);
    }
  };

  const startTikTokEmailExtraction = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/tiktok/accounts/bulk-extract-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          minMedianViews: tiktokExtractEmailsMinViews
        })
      });
      const result = await response.json();
      if (result.success) {
        // Start polling progress
        pollTikTokEmailExtractionProgress();
      }
    } catch (error) {
      console.error('Error starting TikTok email extraction:', error);
    }
  };

  const pollTikTokEmailExtractionProgress = () => {
    const token = localStorage.getItem('authToken');
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/tiktok/accounts/bulk-extract-emails-progress`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const result = await response.json();
        if (result.success) {
          setTiktokExtractEmailsProgress(result.progress);
          if (result.progress.status === 'complete') {
            clearInterval(interval);
            // Refresh accounts list
            fetchAccounts();
          }
        }
      } catch (error) {
        console.error('Error polling TikTok progress:', error);
      }
    }, 2000);
  };

  // Fetch TikTok preview count when modal opens or filters change
  useEffect(() => {
    if (showTikTokExtractEmailsModal) {
      fetchTikTokExtractEmailsPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTikTokExtractEmailsModal, tiktokExtractEmailsMinViews]);

  // Handle sort
  const handleSort = (field: SortField | 'select') => {
    if (field === 'select') return; // select columns are not sortable
    
    const sortableField = field as SortField;
    let newSortOrder: SortOrder;
    
    if (sortField === sortableField) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      localStorage.setItem('creators_sortOrder', newSortOrder);
    } else {
      setSortField(sortableField);
      setSortOrder('desc');
      localStorage.setItem('creators_sortField', sortableField);
      localStorage.setItem('creators_sortOrder', 'desc');
    }
  };

  // Handle search - save to URL for persistence
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleSearch called with:', { searchInput, searchField });
    
    // Update state immediately
    setSearchTerm(searchInput);
    setMinViews(minViewsInput ? parseInt(minViewsInput) : 0);
    setMaxViews(maxViewsInput ? parseInt(maxViewsInput) : null);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Save search state to URL for persistence
    const searchParams = new URLSearchParams(location.search);
    if (searchInput.trim()) {
      searchParams.set('search', searchInput.trim());
      searchParams.set('searchField', searchField);
      searchParams.set('exactMatch', exactMatch.toString());
    } else {
      // Clear search params if empty
      searchParams.delete('search');
      searchParams.delete('searchField');
      searchParams.delete('exactMatch');
    }
    
    // Add views filters to URL if set
    if (minViewsInput && parseInt(minViewsInput) > 0) {
      searchParams.set('minViews', minViewsInput);
    } else {
      searchParams.delete('minViews');
    }
    if (maxViewsInput && parseInt(maxViewsInput) > 0) {
      searchParams.set('maxViews', maxViewsInput);
    } else {
      searchParams.delete('maxViews');
    }
    
    // 새로운 경로 구조에서는 platform 파라미터 불필요
    // 대신 현재 경로를 유지
    const currentPath = location.pathname.includes('/creators/') ? location.pathname : `/creators/${activeTab}`;
    
    navigate(`${currentPath}?${searchParams.toString()}`, { replace: true });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    console.log(`Changing to page ${newPage}`);
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle direct page input
  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(pageInput);
    if (page && page >= 1 && page <= (pagination?.totalPages || 1)) {
      handlePageChange(page);
      setPageInput('');
    }
  };

  // Handle tab change - preserve search state
  const handleTabChange = (tab: 'instagram' | 'tiktok' | 'youtube') => {
    setActiveTab(tab);
    setAccounts([]); // Clear data to trigger fresh fetch
    setPagination({ page: 1, limit: 100, total: 0, totalPages: 0 });
    // DON'T clear search state - this was the bug!
    // setSearchTerm('');
    // setSearchInput('');
    
    // Update URL with new path structure while preserving search params
    const searchParams = new URLSearchParams(location.search);
    // platform 파라미터 제거 (이제 경로로 표현됨)
    searchParams.delete('platform');
    
    // Preserve search state in URL
    if (searchTerm) {
      searchParams.set('search', searchTerm);
      searchParams.set('searchField', searchField);
      searchParams.set('exactMatch', exactMatch.toString());
    }
    
    // 새로운 경로 구조로 이동
    const newPath = `/creators/${tab}`;
    navigate(`${newPath}?${searchParams.toString()}`, { replace: true });
    
    // Clear selections when switching tabs
    setSelectedAccounts(new Set());
    setSelectAll(false);
    setLastSelectedIndex(null);
    // Clear column filters
    setColumnFilters({});
    setShowFilter(null);
  };

  // Handle column width resize
  const handleColumnResize = (columnIndex: number, newWidth: number) => {
    setColumns(prev => prev.map((col, index) => 
      index === columnIndex ? { ...col, width: Math.max(50, newWidth) } : col
    ));
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, columnIndex: number) => {
    setDraggedColumn(columnIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const draggedCol = newColumns[draggedColumn];
    
    // Remove dragged column
    newColumns.splice(draggedColumn, 1);
    
    // Insert at new position
    newColumns.splice(dropIndex, 0, draggedCol);
    
    setColumns(newColumns);
    setDraggedColumn(null);
  };

  // Toggle column visibility
  const toggleColumnVisibility = (index: number) => {
    setColumns(prev => prev.map((col, i) => 
      i === index ? { ...col, visible: !col.visible } : col
    ));
  };

  // Handle drag start for column customizer
  const handleCustomizerDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // Handle drop for column customizer
  const handleCustomizerDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex === dropIndex) return;

    const newColumns = [...columns];
    const [draggedColumn] = newColumns.splice(dragIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);
    setColumns(newColumns);
  };

  const handleCustomizerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch(statusLower) {
      case 'price is too high':
        return '#FBBF24'; // yellow
      case 'needs to negotiate':
        return '#FB923C'; // orange
      case 'high potential':
        return '#A3E635'; // lime
      case 'ready to collaborate':
        return '#059669'; // green
      case 'in negotiation':
        return '#8B5CF6'; // purple
      case 'offer sent':
        return '#94A3B8'; // gray-blue
      case 'in production':
        return '#60A5FA'; // light blue
      case 'done':
        return '#3B82F6'; // blue
      case 'counter offered':
        return '#C084FC'; // light purple
      // Response status colors
      case 'pending':
        return '#F59E0B'; // amber
      case 'missing views':
        return '#EF4444'; // red
      case 'missing audience':
        return '#F87171'; // light red
      case 'incomplete data':
        return '#FB7185'; // rose
      case 'negotiation failed':
        return '#DC2626'; // dark red
      case 'pending_review':
        return '#F97316'; // orange
      case 'price acceptable':
        return '#10B981'; // emerald
      case 'not relevant':
        return '#6B7280'; // gray
      case 'suspicious':
        return '#991B1B'; // dark red
      default:
        return '#94A3B8'; // default gray
    }
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format number
  const formatNumber = (num?: number) => {
    if (!num) return '-';
    return num.toLocaleString();
  };


  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Filter accounts based on column filters and relevancy (status is now handled server-side)
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => {
      return Object.entries(columnFilters).every(([column, filterValues]) => {
        if (filterValues.size === 0) return true;
        const accountValue = (account as any)[column] || 'Empty';
        return filterValues.has(accountValue);
      });
    });
  }, [accounts, columnFilters]);

  // Update selectAll state based on current selections
  useEffect(() => {
    if (filteredAccounts.length > 0) {
      const allSelected = filteredAccounts.every(account => selectedAccounts.has(account._id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedAccounts, filteredAccounts]);

  // Memoize visible columns to avoid filtering per row
  const visibleColumns = useMemo(() => columns.filter(col => col.visible), [columns]);

  // Debug log
  console.log(`Rendering: ${accounts.length} accounts, ${filteredAccounts.length} filtered, page ${pagination?.page}/${pagination?.totalPages}`);

  return (
    <div className="tab-content">
      <div className="page-header">
        {isTargetMode ? (
          <>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/outreach')}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span style={{ fontSize: '18px' }}>←</span> Back
              </button>
              <h1 className="page-title">Email Target Creators</h1>
            </div>
            <p className="page-description">
              Creators with valid email addresses who haven't received emails yet.
            </p>
          </>
        ) : (
          <>
            <h1 className="page-title">Creator Management</h1>
            <p className="page-description">
              Manage influencers across Instagram, TikTok, and YouTube platforms with advanced filtering and organization tools.
            </p>
          </>
        )}
      </div>

      <div className="flex justify-between items-center gap-4 mb-6">
        <div className="flex gap-2">
          <button
            className={`btn ${activeTab === 'instagram' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            onClick={() => handleTabChange('instagram')}
          >
            <span>📸</span>
            <span>Instagram</span>
            {activeTab === 'instagram' && pagination?.total > 0 && (
              <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
                {pagination.total}
              </span>
            )}
          </button>
          <button
            className={`btn ${activeTab === 'tiktok' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            onClick={() => handleTabChange('tiktok')}
          >
            <span>🎵</span>
            <span>TikTok</span>
            {activeTab === 'tiktok' && pagination?.total > 0 && (
              <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
                {pagination.total}
              </span>
            )}
          </button>
          <button
            className={`btn ${activeTab === 'youtube' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            onClick={() => handleTabChange('youtube')}
          >
            <span>📹</span>
            <span>YouTube</span>
            {activeTab === 'youtube' && pagination?.total > 0 && (
              <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
                {pagination.total}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center gap-4" style={{ marginBottom: 'var(--space-6)' }}>
        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex gap-2 items-center">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as 'all' | 'username' | 'email' | 'keyword')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Fields</option>
            <option value="username">Username</option>
            <option value="email">Email</option>
            <option value="keyword">Keyword</option>
          </select>
          <input
            type="text"
            placeholder={`Search ${activeTab === 'tiktok' ? 'TikTok' : activeTab === 'instagram' ? 'Instagram' : 'YouTube'} creators...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-64"
          />
          <button type="submit" className="btn btn-primary px-4 py-2 text-sm">
            Search
          </button>
          <select
            value={emailFilter}
            onChange={(e) => {
              setEmailFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Emails</option>
            <option value="with_email">With Email</option>
            <option value="without_email">Without Email</option>
          </select>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="btn btn-secondary px-3 py-2 text-sm"
            >
              Clear
            </button>
          )}
        </form>


        <div className="flex gap-2">
          {(activeTab === 'instagram' || activeTab === 'tiktok' || activeTab === 'youtube') && (
            <>
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(true);
                  setImportKeywords([]);
                  fetchDistinctKeywords();
                }}
                className="btn btn-secondary flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title="Import creators from other accounts"
              >
                <span>📥</span>
                <span>Import Creators</span>
              </button>
              {(activeTab === 'instagram' || activeTab === 'tiktok') && (
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="btn btn-primary flex items-center gap-2"
                  style={{
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  title={`Add new ${activeTab === 'instagram' ? 'Instagram' : 'TikTok'} creator`}
                >
                  <span>➕</span>
                  <span>Add Creator</span>
                </button>
              )}
            </>
          )}
          <button
            type="button"
            onClick={bulkAnalyzeCreators}
            className="btn btn-primary flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            title="Analyze all unanalyzed creators"
          >
            <span>🔄</span>
            <span>Analyze</span>
          </button>
          <button
            type="button"
            onClick={clearLowQualityCreators}
            className="btn btn-danger flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            title="Delete low quality creators (relevancy=false, score<0.3, failed analysis, no posts)"
          >
            <span>🧹</span>
            <span>Clear Low Quality</span>
          </button>
          {activeTab === 'youtube' && (
            <button
              type="button"
              onClick={() => setShowExtractEmailsModal(true)}
              className="btn btn-primary flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              title="Extract emails for relevant YouTube creators"
            >
              <span>📧</span>
              <span>Extract Emails</span>
            </button>
          )}
          {activeTab === 'tiktok' && (
            <button
              type="button"
              onClick={() => setShowTikTokExtractEmailsModal(true)}
              className="btn btn-primary flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              title="Extract emails for relevant TikTok creators"
            >
              <span>📧</span>
              <span>Extract Emails</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowColumnCustomizer(!showColumnCustomizer)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <span>⚙️</span>
            <span>Columns</span>
          </button>
          <button
            type="button"
            onClick={openCsvExportModal}
            className="btn btn-secondary flex items-center gap-2"
          >
            <span>📥</span>
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {showColumnCustomizer && (
        <div className="column-customizer" style={{ 
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <h4 className="text-lg font-semibold" style={{ margin: '0 0 var(--space-4) 0' }}>Column Settings</h4>
          {columns.map((column, index) => (
            <div 
              key={column.key} 
              draggable
              onDragStart={(e) => handleCustomizerDragStart(e, index)}
              onDragOver={handleCustomizerDragOver}
              onDrop={(e) => handleCustomizerDrop(e, index)}
              className="column-item"
            >
              <span className="text-sm text-secondary" style={{ cursor: 'move', userSelect: 'none' }}>
                ☰
              </span>
              <label className="flex items-center gap-3 flex-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => toggleColumnVisibility(index)}
                  className="rounded"
                />
                <span style={{ 
                  fontWeight: column.visible ? 'var(--font-medium)' : 'var(--font-normal)', 
                  color: column.visible ? 'var(--text-primary)' : 'var(--text-secondary)'
                }}>
                  {column.label}
                </span>
              </label>
              <span className="text-xs text-secondary">
                {column.width}px
              </span>
            </div>
          ))}
          <button
            onClick={() => setShowColumnCustomizer(false)}
            className="btn btn-primary text-sm"
            style={{ marginTop: 'var(--space-4)' }}
          >
            Done
          </button>
        </div>
      )}

      {loading && <div className="loading">Loading...</div>}
      {error && <div className="error">❌ {error}</div>}

      {!loading && !error && (
        <>
          <div className="modern-pagination">
            <div className="pagination-info">
              Showing {filteredAccounts.length} of {pagination?.total || 0} {activeTab} creators {hasActiveFilters && '(filtered)'}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={(pagination?.page || 1) === 1}
                className="pagination-button"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange((pagination?.page || 1) - 1)}
                disabled={(pagination?.page || 1) === 1}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="page-info">
                Page 
                <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                  <input
                    type="number"
                    value={pageInput || (pagination?.page || 1)}
                    onChange={(e) => setPageInput(e.target.value)}
                    min="1"
                    max={pagination?.totalPages || 1}
                    className="input text-center"
                    style={{ 
                      width: '60px',
                      padding: 'var(--space-1) var(--space-2)'
                    }}
                  />
                </form>
                of {pagination?.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange((pagination?.page || 1) + 1)}
                disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                className="pagination-button"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(pagination?.totalPages || 1)}
                disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                className="pagination-button"
              >
                Last
              </button>
            </div>
          </div>

          <div className="table-container" style={{ margin: 'var(--space-6) 0' }}>
            <table className="modern-table" ref={tableRef}>
              <thead>
                <tr>
                  {visibleColumns.map((column, index) => (
                    <th
                      key={column.key}
                      style={{ 
                        width: `${column.width}px`,
                        padding: '18px 12px'
                      }}
                      onClick={() => handleSort(column.key)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`resizable-header ${draggedColumn === index ? 'dragging' : ''}`}
                    >
                      <div className="header-content">
                        <span className="header-text">
                          {column.key === 'select' ? (
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={toggleSelectAll}
                              className="rounded"
                            />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>
                                {column.label} {(['username', 'email', 'keyword', 'averageViews', 'medianViews', 'status', 'updatedAt', 'emailSentAt', 'createdAt'] as const).includes(column.key as any) && getSortIcon(column.key as SortField)}
                              </span>
                              {(['email', 'keyword', 'status'] as const).includes(column.key as any) && (
                                <div style={{ position: 'relative' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowFilter(showFilter === column.key ? null : column.key);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '2px',
                                      color: columnFilters[column.key] ? '#4dabf7' : 'var(--text-secondary)',
                                      fontSize: '12px'
                                    }}
                                  >
                                    🔽
                                  </button>
                                  {showFilter === column.key && (
                                    <div
                                      className="filter-dropdown"
                                      style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: '0',
                                        background: 'var(--bg-primary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        minWidth: '200px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        zIndex: 1001,
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 'bold' }}>
                                        Filter {column.label}
                                      </div>
                                      {columnFilters[column.key] && columnFilters[column.key].size > 0 && (
                                        <button
                                          onClick={() => clearColumnFilter(column.key)}
                                          style={{
                                            fontSize: '11px',
                                            padding: '2px 6px',
                                            marginBottom: '6px',
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '3px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Clear
                                        </button>
                                      )}
                                      {(filterOptions[column.key] || []).map(option => (
                                        <label
                                          key={option}
                                          style={{
                                            display: 'block',
                                            padding: '4px 0',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={columnFilters[column.key]?.has(option) || false}
                                            onChange={() => toggleColumnFilter(column.key, option)}
                                            style={{ marginRight: '6px' }}
                                          />
                                          {option}
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </span>
                        <div 
                          className="resize-handle"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const startX = e.clientX;
                            const startWidth = column.width;
                            
                            const handleMouseMove = (e: MouseEvent) => {
                              const diff = e.clientX - startX;
                              handleColumnResize(index, startWidth + diff);
                            };
                            
                            const handleMouseUp = () => {
                              document.removeEventListener('mousemove', handleMouseMove);
                              document.removeEventListener('mouseup', handleMouseUp);
                            };
                            
                            document.addEventListener('mousemove', handleMouseMove);
                            document.addEventListener('mouseup', handleMouseUp);
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account, accountIndex) => (
                  <tr
                    key={account._id}
                    style={{
                      backgroundColor: selectedAccounts.has(account._id) ? 'rgba(34, 197, 94, 0.05)' : undefined,
                      cursor: 'default'
                    }}
                    onClick={(e) => {
                      // Handle row click for selection if not clicking on username or checkbox
                      const target = e.target as HTMLElement;
                      if (!target.closest('.username-cell') && !target.closest('input[type="checkbox"]')) {
                        toggleSelectAccount(account._id, accountIndex, e);
                      }
                    }}
                  >
                    {visibleColumns.map((column) => (
                      <td 
                        key={`${account._id}-${column.key}`}
                        style={{ 
                          width: `${column.width}px`,
                          cursor: column.key === 'username' ? 'default' : 'pointer',
                          padding: '18px 12px',
                          verticalAlign: 'middle'
                        }}
                        className={`${column.key}-cell`}
                      >
                        {column.key === 'select' && (
                          <input
                            type="checkbox"
                            checked={selectedAccounts.has(account._id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSelectAccount(account._id, accountIndex, e);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded"
                            style={{ pointerEvents: 'auto' }}
                          />
                        )}
                        {column.key === 'username' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // YouTube uses channelName instead of username
                              const displayName = account.channelName || account.username;
                              const identifier = account.channelId || account.username;
                              navigate(`/creators/${(account.platform || activeTab).toLowerCase()}/${identifier}`);
                            }}
                            style={{
                              fontWeight: 'bold',
                              color: '#4dabf7',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              padding: 0,
                              textDecoration: 'underline'
                            }}
                          >
                            {account.channelName || account.username}
                          </button>
                        )}
                        {column.key === 'platform' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const platformName = (account.platform || activeTab).toLowerCase();
                              if (platformName === 'tiktok') {
                                window.open(`https://www.tiktok.com/@${account.username}`, '_blank');
                              } else if (platformName === 'instagram') {
                                window.open(`https://www.instagram.com/${account.username}`, '_blank');
                              } else if (platformName === 'youtube') {
                                window.open(`https://www.youtube.com/channel/${account.channelId}`, '_blank');
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: (account.platform || activeTab).toLowerCase() === 'tiktok' ? '#000000' :
                                             (account.platform || activeTab).toLowerCase() === 'instagram' ? '#E4405F' : '#FF0000',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              textTransform: 'capitalize',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.opacity = '0.8';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title={`Open ${account.channelName || account.username} on ${(account.platform || activeTab)}`}
                          >
                            {(account.platform || activeTab).toLowerCase() === 'tiktok' ? '🎵' :
                             (account.platform || activeTab).toLowerCase() === 'instagram' ? '📷' : '📹'}
                            {(account.platform || activeTab).charAt(0).toUpperCase() + (account.platform || activeTab).slice(1).toLowerCase()}
                          </button>
                        )}
                        {column.key === 'email' && (account.email || '-')}
                        {column.key === 'keyword' && (account.keyword || '-')}
                        {column.key === 'averageViews' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>{formatNumber(account.averageViews || account.subscriberCount)}</span>
                            {account.postsDateRange && (
                              <span style={{ fontSize: '10px', color: '#888' }}>
                                {new Date(account.postsDateRange.from).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ~
                                {new Date(account.postsDateRange.to).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            {activeTab === 'youtube' && account.subscriberCount && (
                              <span style={{ fontSize: '10px', color: '#888' }}>
                                {formatNumber(account.subscriberCount)} subs
                              </span>
                            )}
                          </div>
                        )}
                        {column.key === 'medianViews' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>{formatNumber(account.medianViews)}</span>
                            {account.lastPostsUpdate && (
                              <span style={{ fontSize: '10px', color: '#888' }}>
                                최근 30일
                              </span>
                            )}
                          </div>
                        )}
                        {column.key === 'relevancy' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span 
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: account.relevancy ? '#e7f5e7' : '#fdf2f2',
                                color: account.relevancy ? '#2d5a2d' : '#8b4513',
                                border: `1px solid ${account.relevancy ? '#b8d4b8' : '#e5b8b8'}`,
                                cursor: account.relevancyReason ? 'help' : 'default'
                              }}
                              title={
                                account.relevancyReason 
                                  ? `${account.relevancy ? 'Relevant' : 'Not Relevant'}${account.relevancyScore ? ` (${Math.round(account.relevancyScore * 100)}%)` : ''}\n\nReason: ${account.relevancyReason}${
                                      account.relevantKeywords && Object.keys(account.relevantKeywords).length > 0 
                                        ? `\n\nRelated Keywords: ${Object.keys(account.relevantKeywords).join(', ')}` 
                                        : ''
                                    }`
                                  : account.relevancy ? 'Marked as relevant' : 'Marked as not relevant'
                              }
                            >
                              {account.relevancy ? 'Relevant' : 'Not Relevant'}
                              {account.relevancyScore && (
                                <span style={{
                                  fontSize: '10px',
                                  opacity: 0.8,
                                  marginLeft: '4px'
                                }}>
                                  ({Math.round(account.relevancyScore * 100)}%)
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        {column.key === 'status' && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: account.status ? `${getStatusColor(account.status)}20` : 'transparent',
                              color: account.status ? getStatusColor(account.status) : 'var(--text-secondary)',
                              border: account.status ? `1px solid ${getStatusColor(account.status)}40` : '1px solid var(--border-color)'
                            }}
                          >
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: getStatusColor(account.status || ''),
                                flexShrink: 0
                              }}
                            />
                            {account.status || '-'}
                          </span>
                        )}
                        {column.key === 'responseStatus' && (
                          account.responseStatus ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: `${getStatusColor(account.responseStatus)}20`,
                                color: getStatusColor(account.responseStatus),
                                border: `1px solid ${getStatusColor(account.responseStatus)}40`,
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (account.responseId) {
                                  window.open(`/outreach/responses?search=${account.username}`, '_blank');
                                }
                              }}
                              title={`Click to view response details`}
                            >
                              <span
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: getStatusColor(account.responseStatus),
                                  flexShrink: 0
                                }}
                              />
                              {account.responseStatus}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>
                          )
                        )}
                        {column.key === 'source' && (
                          <span 
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: '#f8f9fa',
                              color: '#495057',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            {account.source || 'manual_add'}
                          </span>
                        )}
                        {column.key === 'emailSentAt' && (
                          account.emailSentAt ? (
                            formatDate(account.emailSentAt)
                          ) : (
                            account.email ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEmailModal(account);
                                }}
                                disabled={sendingEmail === account._id}
                                style={{
                                  background: sendingEmail === account._id 
                                    ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                                    : 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  color: 'white',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  cursor: sendingEmail === account._id ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease',
                                  boxShadow: sendingEmail === account._id 
                                    ? '0 1px 3px rgba(148, 163, 184, 0.3)'
                                    : '0 1px 3px rgba(34, 197, 94, 0.3)',
                                  opacity: sendingEmail === account._id ? 0.7 : 1
                                }}
                                onMouseOver={(e) => {
                                  if (sendingEmail !== account._id) {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(34, 197, 94, 0.4)';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (sendingEmail !== account._id) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(34, 197, 94, 0.3)';
                                  }
                                }}
                                title={sendingEmail === account._id ? 'Sending email...' : `Send email to ${account.email}`}
                              >
                                <span>{sendingEmail === account._id ? '⏳' : '📧'}</span>
                                <span>{sendingEmail === account._id ? 'Sending...' : 'Send Email'}</span>
                              </button>
                            ) : (
                              <span style={{ 
                                color: 'var(--text-secondary)', 
                                fontSize: '12px',
                                fontStyle: 'italic'
                              }}>
                                No email
                              </span>
                            )
                          )
                        )}
                        {column.key === 'createdAt' && formatDate(account.createdAt)}
                        {column.key === 'updatedAt' && formatDate(account.updatedAt)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="pagination-info">
              Showing {filteredAccounts.length} of {pagination?.total || 0} accounts {hasActiveFilters && '(filtered)'}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={(pagination?.page || 1) === 1}
                className="pagination-button"
              >
                ⏮️ First
              </button>
              <button
                onClick={() => handlePageChange((pagination?.page || 1) - 1)}
                disabled={(pagination?.page || 1) === 1}
                className="pagination-button"
              >
                ◀️ Previous
              </button>
              <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                Page 
                <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                  <input
                    type="number"
                    value={pageInput || (pagination?.page || 1)}
                    onChange={(e) => setPageInput(e.target.value)}
                    min="1"
                    max={pagination?.totalPages || 1}
                    style={{ 
                      width: '50px', 
                      padding: '2px 4px', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                  />
                </form>
                of {pagination?.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange((pagination?.page || 1) + 1)}
                disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                className="pagination-button"
              >
                Next ▶️
              </button>
              <button
                onClick={() => handlePageChange(pagination?.totalPages || 1)}
                disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                className="pagination-button"
              >
                Last ⏭️
              </button>
            </div>
          </div>
        </>
      )}

      {/* Floating Selection UI - using Portal */}
      {selectedAccounts.size > 0 && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-4) var(--space-6)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            backdropFilter: 'blur(12px)',
            minWidth: '280px'
          }}
          className="floating-selection-bar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              {selectedAccounts.size || 0} selected
            </span>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-secondary)',
              marginLeft: '12px',
              opacity: 0.8
            }}>
              (Shift+Click: range • {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Click: multiple)
            </span>
          </div>
          
          <button
            onClick={() => updateSelectedCreatorsRelevancy(true)}
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.3)';
            }}
          >
            <span>✓</span>
            <span>Mark Relevant</span>
          </button>

          <button
            onClick={() => updateSelectedCreatorsRelevancy(false)}
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
            }}
          >
            <span>✗</span>
            <span>Mark Not Relevant</span>
          </button>

          <button
            onClick={reanalyzeSelectedCreators}
            style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(79, 172, 254, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.3)';
            }}
          >
            <span>🔄</span>
            <span>Analyze</span>
          </button>

          <button
            onClick={deleteSelectedCreators}
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 107, 0.3)';
            }}
          >
            <span>🗑️</span>
            <span>Delete Selected</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedAccounts(new Set());
              setSelectAll(false);
              setLastSelectedIndex(null);
            }}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Clear selection"
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}

      {/* Creator CRUD Modal */}
      {!isTargetMode && showCreatorModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)',
            paddingTop: '80px',
            overflowY: 'auto'
          }}
          onClick={() => setShowCreatorModal(false)}
        >
          <div 
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 'var(--space-8)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-primary">
                {editingCreator ? 'Edit Creator' : 'Create New Creator'}
              </h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreatorModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {!editingCreator && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Platform <span className="text-error">*</span>
                  </label>
                  <select
                    value={creatorForm.platform}
                    onChange={(e) => setCreatorForm(prev => ({ ...prev, platform: e.target.value as 'instagram' | 'tiktok' }))}
                    className="input w-full"
                  >
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  Username <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={creatorForm.username}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, username: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={creatorForm.email}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, email: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Keyword
                </label>
                <input
                  type="text"
                  value={creatorForm.keyword}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, keyword: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter keyword"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={creatorForm.description}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter description"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Average Views
                </label>
                <input
                  type="number"
                  value={creatorForm.averageViews}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, averageViews: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter average views"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Status
                </label>
                <select
                  value={creatorForm.status}
                  onChange={(e) => setCreatorForm(prev => ({ ...prev, status: e.target.value }))}
                  className="input w-full"
                >
                  <option value="">Select status</option>
                  <option value="price is too high">Price is too high</option>
                  <option value="needs to negotiate">Needs to negotiate</option>
                  <option value="high potential">High potential</option>
                  <option value="ready to collaborate">Ready to collaborate</option>
                  <option value="In negotiation">In negotiation</option>
                  <option value="offer sent">Offer sent</option>
                  <option value="In production">In production</option>
                  <option value="done">Done</option>
                  <option value="counter offered">Counter offered</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={creatorForm.relevancy}
                    onChange={(e) => setCreatorForm(prev => ({ ...prev, relevancy: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Relevant</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                className="btn btn-primary flex-1"
                onClick={editingCreator ? updateCreator : createCreator}
                disabled={!creatorForm.username.trim()}
              >
                {editingCreator ? 'Update Creator' : 'Create Creator'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreatorModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import From Others Modal */}
      {showImportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)',
            paddingTop: '80px',
            overflowY: 'auto'
          }}
          onClick={() => setShowImportModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-8)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: 0
              }}>
                Import Creators From Other Accounts
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Minimum Median Views Input */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Minimum Median Views
              </label>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <input
                  type="number"
                  value={minMedianViews}
                  onChange={(e) => setMinMedianViews(parseInt(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border rounded-md"
                  placeholder="Enter minimum median views"
                  min={0}
                />
                <button
                  type="button"
                  onClick={() => fetchDistinctKeywords(minMedianViews, importOnlyWithEmail)}
                  disabled={isLoadingKeywords}
                  className="btn btn-secondary px-4"
                  style={{
                    opacity: isLoadingKeywords ? 0.5 : 1,
                    cursor: isLoadingKeywords ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoadingKeywords ? 'Loading...' : 'Apply'}
                </button>
              </div>
            </div>

            {/* Only with email checkbox */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <input
                  type="checkbox"
                  checked={importOnlyWithEmail}
                  onChange={(e) => setImportOnlyWithEmail(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.875rem' }}>Only import creators with email</span>
              </label>
            </div>

            {/* Keywords Selection */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Select Keywords ({importKeywords.length} selected)
              </label>
              {isLoadingKeywords ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>
                  Loading keywords...
                </div>
              ) : availableKeywords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>
                  No keywords found
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '8px',
                    padding: 'var(--space-2)'
                  }}
                >
                  {availableKeywords.map((kw) => (
                    <div key={kw.keyword}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2)',
                          borderRadius: '4px'
                        }}
                        className="hover:bg-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={importKeywords.includes(kw.keyword)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setImportKeywords(prev => [...prev, kw.keyword]);
                            } else {
                              setImportKeywords(prev => prev.filter(k => k !== kw.keyword));
                            }
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span style={{ flex: 1, color: 'var(--text-primary)', cursor: 'pointer' }} onClick={() => {
                          setImportKeywords(prev =>
                            prev.includes(kw.keyword)
                              ? prev.filter(k => k !== kw.keyword)
                              : [...prev, kw.keyword]
                          );
                        }}>{kw.keyword}</span>
                        <span
                          style={{
                            fontSize: '0.75rem',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedKeyword(expandedKeyword === kw.keyword ? null : kw.keyword);
                          }}
                        >
                          ({kw.count.toLocaleString()} creators)
                        </span>
                      </div>
                      {expandedKeyword === kw.keyword && (
                        <div
                          style={{
                            marginLeft: '28px',
                            padding: 'var(--space-2)',
                            background: 'var(--bg-secondary)',
                            borderRadius: '4px',
                            marginBottom: 'var(--space-2)',
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {kw.usernames.map((username) => (
                            <a
                              key={username}
                              href={activeTab === 'instagram'
                                ? `https://instagram.com/${username}`
                                : activeTab === 'youtube'
                                  ? `https://youtube.com/@${username}`
                                  : `https://tiktok.com/@${username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'block',
                                fontSize: '0.75rem',
                                color: '#3b82f6',
                                padding: '2px 0',
                                textDecoration: 'none'
                              }}
                              className="hover:underline"
                            >
                              @{username}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
              <button
                className="btn btn-primary flex-1"
                onClick={executeImport}
                disabled={importKeywords.length === 0 || isImporting}
                style={{
                  opacity: importKeywords.length === 0 || isImporting ? 0.5 : 1,
                  cursor: importKeywords.length === 0 || isImporting ? 'not-allowed' : 'pointer'
                }}
              >
                {isImporting ? 'Importing...' : `Import Creators`}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowImportModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedCreator && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)'
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-8)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: 0
              }}>
                Send Email to @{selectedCreator.username}
              </h3>
              <button 
                onClick={() => setShowEmailModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {/* Creator Info */}
              <div style={{
                background: 'var(--bg-secondary)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <span style={{ fontSize: '1.25rem' }}>
                    {selectedCreator.platform === 'tiktok' ? '🎵' : '📸'}
                  </span>
                  <div>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                      @{selectedCreator.username}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      {selectedCreator.platform?.toUpperCase()} • {formatNumber(selectedCreator.averageViews)} avg views
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Address */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '500', 
                  marginBottom: 'var(--space-2)',
                  color: 'var(--text-primary)'
                }}>
                  Email Address <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="creator@example.com"
                  className="input"
                  style={{ width: '100%' }}
                  autoFocus
                />
              </div>

              {/* Template Selection */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '500', 
                  marginBottom: 'var(--space-2)',
                  color: 'var(--text-primary)'
                }}>
                  Email Template <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                {loadingTemplates ? (
                  <div style={{ 
                    padding: 'var(--space-4)', 
                    textAlign: 'center', 
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem'
                  }}>
                    Loading templates...
                  </div>
                ) : templates.length > 0 ? (
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="input"
                    style={{ width: '100%' }}
                  >
                    <option value="">Select a template</option>
                    {templates.map(template => (
                      <option key={template._id} value={template._id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ 
                    padding: 'var(--space-4)', 
                    textAlign: 'center', 
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    background: 'var(--bg-tertiary)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-color)'
                  }}>
                    No email templates found. Please create templates in the Outreach section.
                  </div>
                )}
              </div>

              {/* Template Preview */}
              {selectedTemplate && selectedCreator && (
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.875rem', 
                    fontWeight: '500', 
                    marginBottom: 'var(--space-2)',
                    color: 'var(--text-primary)'
                  }}>
                    Email Preview (as {selectedCreator.username} will receive)
                  </label>
{(() => {
                    const template = templates.find(t => t._id === selectedTemplate);
                    if (!template || !selectedCreator) {
                      return (
                        <div style={{
                          background: 'var(--bg-tertiary)',
                          padding: 'var(--space-4)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-color)',
                          fontSize: '0.875rem',
                          color: 'var(--text-secondary)',
                          fontStyle: 'italic'
                        }}>
                          Select a template to see preview
                        </div>
                      );
                    }

                    // Prepare variables for substitution
                    const templateVariables: Record<string, string> = {
                      name: selectedCreator.username,
                      handle: `@${selectedCreator.username}`,
                      email: emailAddress,
                      platform: selectedCreator.platform || activeTab,
                      username: selectedCreator.username,
                      averageViews: selectedCreator.averageViews?.toString() || '0',
                      followerCount: (selectedCreator as any).followerCount?.toString() || 'N/A'
                    };

                    // Substitute variables in subject and content
                    const processedSubject = substituteTemplateVariables(template.subject || '', templateVariables);
                    const processedContent = substituteTemplateVariables(template.content || '', templateVariables);
                    
                    // Convert HTML to plain text
                    const plainTextContent = stripHtmlTags(processedContent);

                    return (
                      <div style={{
                        background: 'var(--bg-tertiary)',
                        padding: 'var(--space-4)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)'
                      }}>
                        {/* Subject Line */}
                        <div style={{ 
                          marginBottom: 'var(--space-3)',
                          paddingBottom: 'var(--space-2)',
                          borderBottom: '1px solid var(--border-color)'
                        }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: 'var(--text-tertiary)', 
                            marginBottom: 'var(--space-1)',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                          }}>
                            Subject Line
                          </div>
                          <div style={{ 
                            fontWeight: '500', 
                            color: 'var(--text-primary)',
                            fontSize: '0.875rem'
                          }}>
                            {processedSubject || 'No subject'}
                          </div>
                        </div>

                        {/* Email Content */}
                        <div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: 'var(--text-tertiary)', 
                            marginBottom: 'var(--space-2)',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                          }}>
                            Email Content
                          </div>
                          <div style={{ 
                            maxHeight: '300px', 
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.8rem',
                            lineHeight: '1.5',
                            color: 'var(--text-primary)',
                            background: 'var(--bg-primary)',
                            padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-color)'
                          }}>
                            {plainTextContent || 'No content'}
                          </div>
                        </div>
                        
                        {/* Variables Used */}
                        <div style={{ 
                          marginTop: 'var(--space-3)',
                          paddingTop: 'var(--space-2)',
                          borderTop: '1px solid var(--border-color)'
                        }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: 'var(--text-tertiary)', 
                            marginBottom: 'var(--space-1)',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                          }}>
                            Variables Used
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)'
                          }}>
                            {Object.entries(templateVariables).map(([key, value]) => (
                              <span key={key} style={{
                                background: 'var(--bg-secondary)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                border: '1px solid var(--border-color)',
                                fontSize: '0.7rem'
                              }}>
                                {key}: <strong>{value}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Test Email Section */}
            {showTestEmailInput && (
              <div style={{
                marginTop: 'var(--space-4)',
                padding: 'var(--space-4)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '500', 
                  marginBottom: 'var(--space-2)',
                  color: 'var(--text-primary)'
                }}>
                  Send Test Email
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="test@example.com"
                    className="input"
                    style={{ flex: 1 }}
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && testEmailAddress && testEmailAddress.includes('@')) {
                        handleSendTestEmail();
                      }
                    }}
                  />
                  <button 
                    onClick={handleSendTestEmail}
                    disabled={sendingTestEmail || !testEmailAddress || !testEmailAddress.includes('@') || !selectedTemplate}
                    className="btn btn-primary"
                    style={{
                      opacity: (sendingTestEmail || !testEmailAddress || !testEmailAddress.includes('@') || !selectedTemplate) ? 0.5 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {sendingTestEmail ? 'Sending...' : 'Send Test'}
                  </button>
                  <button 
                    onClick={() => {
                      setShowTestEmailInput(false);
                      setTestEmailAddress('');
                    }}
                    className="btn btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Cancel
                  </button>
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-secondary)', 
                  marginTop: 'var(--space-1)'
                }}>
                  This will send a test email with {selectedCreator?.username}'s information to the address above.
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div style={{ 
              display: 'flex', 
              gap: 'var(--space-3)', 
              marginTop: 'var(--space-6)',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={() => setShowEmailModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={() => setShowTestEmailInput(!showTestEmailInput)}
                className="btn btn-outline"
                disabled={!selectedTemplate}
                style={{
                  opacity: !selectedTemplate ? 0.5 : 1,
                  background: showTestEmailInput ? 'var(--bg-secondary)' : 'transparent',
                  border: '1px solid var(--border-color)'
                }}
              >
                {showTestEmailInput ? 'Hide Test Email' : 'Send Test Email'}
              </button>
              <button 
                onClick={handleSendEmail}
                disabled={sendingEmail === selectedCreator._id || !emailAddress || !selectedTemplate || !emailAddress.includes('@')}
                className="btn btn-primary"
                style={{
                  opacity: (sendingEmail === selectedCreator._id || !emailAddress || !selectedTemplate || !emailAddress.includes('@')) ? 0.5 : 1
                }}
              >
                {sendingEmail === selectedCreator._id ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CSV Export Modal */}
      {showCsvExportModal && createPortal(
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ margin: '0 0 var(--space-4) 0', fontSize: '1.25rem' }}>
              Export to CSV
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', fontSize: '0.875rem' }}>
              Select columns to include in the export ({filteredAccounts.length} rows)
            </p>

            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
              <button
                onClick={selectAllCsvColumns}
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
              >
                Select All
              </button>
              <button
                onClick={deselectAllCsvColumns}
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
              >
                Deselect All
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-6)'
            }}>
              {columns.filter(col => col.key !== 'select').map(column => (
                <label
                  key={column.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    background: csvExportColumns.includes(column.key) ? 'var(--bg-secondary)' : 'transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={csvExportColumns.includes(column.key)}
                    onChange={() => toggleCsvColumn(column.key)}
                  />
                  <span style={{ fontSize: '0.875rem' }}>{column.label}</span>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCsvExportModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={exportToCsv}
                className="btn btn-primary"
                disabled={csvExportColumns.length === 0}
                style={{ opacity: csvExportColumns.length === 0 ? 0.5 : 1 }}
              >
                Export ({csvExportColumns.length} columns)
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Extract Emails Modal (YouTube only) */}
      {showExtractEmailsModal && createPortal(
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ margin: '0 0 var(--space-4) 0', fontSize: '1.25rem' }}>
              📧 Extract Emails
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', fontSize: '0.875rem' }}>
              Extract emails for relevant YouTube creators without email addresses.
            </p>

            {/* Settings */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500' }}>
                Minimum Median Views
              </label>
              <input
                type="number"
                value={extractEmailsMinViews}
                onChange={(e) => setExtractEmailsMinViews(parseInt(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500' }}>
                Country Filter
              </label>
              <select
                value={extractEmailsCountry}
                onChange={(e) => setExtractEmailsCountry(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">All Countries</option>
                <option value="US">United States</option>
                <option value="KR">South Korea</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="JP">Japan</option>
                <option value="IN">India</option>
                <option value="BR">Brazil</option>
              </select>
            </div>

            {/* Preview Count */}
            <div style={{
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-secondary)',
              marginBottom: 'var(--space-4)',
              textAlign: 'center'
            }}>
              {extractEmailsLoading ? (
                <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
              ) : (
                <span>
                  <strong style={{ fontSize: '1.25rem', color: 'var(--text-primary)' }}>
                    {extractEmailsPreviewCount}
                  </strong>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 'var(--space-2)' }}>
                    accounts match criteria
                  </span>
                </span>
              )}
            </div>

            {/* Progress */}
            {extractEmailsProgress && extractEmailsProgress.status !== 'idle' && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                  marginBottom: 'var(--space-2)'
                }}>
                  <div style={{
                    width: `${extractEmailsProgress.progress}%`,
                    height: '100%',
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>{extractEmailsProgress.current} / {extractEmailsProgress.total} processed</span>
                  <span>
                    ✅ {extractEmailsProgress.success} found | ❌ {extractEmailsProgress.failed} not found
                  </span>
                </div>
                {extractEmailsProgress.status === 'complete' && (
                  <div style={{
                    marginTop: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: '#22c55e20',
                    color: '#22c55e',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}>
                    Extraction complete!
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowExtractEmailsModal(false);
                  setExtractEmailsProgress(null);
                }}
                className="btn btn-secondary"
              >
                {extractEmailsProgress?.status === 'complete' ? 'Close' : 'Cancel'}
              </button>
              {extractEmailsProgress?.status !== 'processing' && extractEmailsProgress?.status !== 'complete' && (
                <button
                  onClick={startEmailExtraction}
                  className="btn btn-primary"
                  disabled={extractEmailsPreviewCount === 0 || extractEmailsLoading}
                  style={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                    opacity: extractEmailsPreviewCount === 0 || extractEmailsLoading ? 0.5 : 1
                  }}
                >
                  Start Extraction
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Extract Emails Modal (TikTok) */}
      {showTikTokExtractEmailsModal && createPortal(
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ margin: '0 0 var(--space-4) 0', fontSize: '1.25rem' }}>
              📧 Extract TikTok Emails
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', fontSize: '0.875rem' }}>
              Extract emails for relevant TikTok creators without email addresses.
            </p>

            {/* Settings */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500' }}>
                Minimum Median Views
              </label>
              <input
                type="number"
                value={tiktokExtractEmailsMinViews}
                onChange={(e) => setTiktokExtractEmailsMinViews(parseInt(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)'
                }}
              />
            </div>

            {/* Preview Count */}
            <div style={{
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-secondary)',
              marginBottom: 'var(--space-4)',
              textAlign: 'center'
            }}>
              {tiktokExtractEmailsLoading ? (
                <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
              ) : (
                <span>
                  <strong style={{ fontSize: '1.25rem', color: 'var(--text-primary)' }}>
                    {tiktokExtractEmailsPreviewCount}
                  </strong>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 'var(--space-2)' }}>
                    accounts match criteria
                  </span>
                </span>
              )}
            </div>

            {/* Progress */}
            {tiktokExtractEmailsProgress && tiktokExtractEmailsProgress.status !== 'idle' && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                  marginBottom: 'var(--space-2)'
                }}>
                  <div style={{
                    width: `${tiktokExtractEmailsProgress.progress}%`,
                    height: '100%',
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>{tiktokExtractEmailsProgress.current} / {tiktokExtractEmailsProgress.total} processed</span>
                  <span>
                    ✅ {tiktokExtractEmailsProgress.success} found | ❌ {tiktokExtractEmailsProgress.failed} not found
                  </span>
                </div>
                {tiktokExtractEmailsProgress.status === 'complete' && (
                  <div style={{
                    marginTop: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: '#22c55e20',
                    color: '#22c55e',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}>
                    Extraction complete!
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowTikTokExtractEmailsModal(false);
                  setTiktokExtractEmailsProgress(null);
                }}
                className="btn btn-secondary"
              >
                {tiktokExtractEmailsProgress?.status === 'complete' ? 'Close' : 'Cancel'}
              </button>
              {tiktokExtractEmailsProgress?.status !== 'processing' && tiktokExtractEmailsProgress?.status !== 'complete' && (
                <button
                  onClick={startTikTokEmailExtraction}
                  className="btn btn-primary"
                  disabled={tiktokExtractEmailsPreviewCount === 0 || tiktokExtractEmailsLoading}
                  style={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                    opacity: tiktokExtractEmailsPreviewCount === 0 || tiktokExtractEmailsLoading ? 0.5 : 1
                  }}
                >
                  Start Extraction
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Creators;
